import axios from 'axios';
import qs from 'query-string';
import { store } from 'shared/store';
import { makeLog } from 'shared/utils/log';
import { checkAccount, selectActiveOrg } from 'shared/store/auth-slice';
import { ErrorCode, errorLanguage } from 'types';
import { getStoredAuthenticationToken } from 'shared/store/storage';
import { TOKEN_AUTHENTICATION_HEADER } from 'shared/utils/authentication-token';
import { receivedServerBuildInfo, serverTimeReceived } from 'shared/store/realtime-slice';
import { setIsNewVersionAvailable } from 'shared/store/dashboard-slice';
import { getCommitHash } from 'sb-lib/env';
import { message } from 'antd';

const log = makeLog('axios');

const instance = axios.create({
  // If API_PROXY is enabled, just hit relative path with the assumption that
  // a proxy has been configured to send the request to the backend.
  baseURL: process.env.API_PROXY ? '/' : process.env.BACKEND_URL,
  headers: {},
  // for cookies
  withCredentials: true,
  // Quit on any request if it takes longer than 15s
  timeout: 15000,
  // Arrays as multiple repeated args, not array bracketed
  paramsSerializer: p => qs.stringify(p, { arrayFormat: 'none' }),
});

let lastAuthed = null as null | string;

export type RejectError = {
  message: string | null;
  errorCode?: number;
};

instance.interceptors.request.use(req => {
  // Add the active org id as a header to all requests, if available
  if (store) {
    const orgId = selectActiveOrg(store.getState())?.id;
    if (orgId) {
      req.headers['X-ORG-ID'] = orgId;
    }
  }
  // Add authentication token if we have one
  const authToken = getStoredAuthenticationToken();
  if (authToken) {
    req.headers[TOKEN_AUTHENTICATION_HEADER] = authToken;
    // Used for authenticating public salesfloor users, who rely on a separate auth mechanism
    req.headers['Authorization'] = `Bearer ${authToken}`;
  }

  (req as any).timeoutMessage = setTimeout(() => {
    message.warn(`Please wait, still processing...`);
  }, 10000);
  return req;
});

instance.interceptors.response.use(
  res => {
    // - watch for changes to auth header and trigger checkUser action if it changes
    // - this allows for external authorization events to be registered in this context
    // - external auth events include login/logout in another tab
    const authed = res.headers['x-account-authed'];
    if (lastAuthed && lastAuthed !== authed) {
      if (store) {
        log.debug(
          `Account change detected via header ${lastAuthed} => ${authed}, will checkAccount`,
        );
        store.dispatch(checkAccount());
      }
    }
    lastAuthed = authed;

    // give redux store server commit hash and protocol version
    const serverCommitHash = res.headers['x-commit-hash'];
    const serverProtocolVersion = parseInt(res.headers['x-protocol-version'] || '0');
    const db = store.getState().realtime;
    if (
      db.serverCommitHash !== serverCommitHash ||
      db.serverProtocolVersion !== serverProtocolVersion
    ) {
      log.debug(
        `Server has commit hash ${serverCommitHash}, and protocol version ${serverProtocolVersion}`,
      );
      store.dispatch(
        receivedServerBuildInfo({
          commitHash: serverCommitHash,
          protocolVersion: serverProtocolVersion,
        }),
      );
    }

    // Check for new frontend version if backend version is different
    if (getCommitHash() !== serverCommitHash) {
      checkForNewVersion();
    }

    // time delta with server
    if (db.serverTimeDelta === null) {
      const serverTime = parseInt(res.headers['x-server-time'] || 0);
      if (serverTime) {
        store.dispatch(serverTimeReceived(serverTime));
      }
    }

    clearTimeout((res.config as any).timeoutMessage);

    return res;
  },

  // Try to parse error message if possible
  err => {
    if (err.response && err.response.data) {
      const resCode = err.response.data.code as ErrorCode;
      const resMessage = err.response.data.message;
      const resData = err.response.data.data;
      err.message = null as null | string;
      err.code = null as null | ErrorCode;

      // 1st try code to language message
      if (resCode) {
        const code = err.response.data.code as ErrorCode;
        if (errorLanguage[code]) {
          err.message = errorLanguage[code];
        } else {
          log.log(`axios.ts:response - no language set for error code ${code}`);
        }
        err.code = code;
      }
      // 2nd if message not set
      if (!err.message) {
        err.message = resMessage || resData || 'no message found for error';
      }
    }
    if (err.config) {
      clearTimeout((err.config as any).timeoutMessage);
    }
    return Promise.reject(err as RejectError);
  },
);

const CHECK_VERSION_COOLDOWN = 15 * 1000; // 15 sec
let lastCheckedForNewVersion = 0;
let newVersionCheckTimeout: NodeJS.Timeout | null = null;
function checkForNewVersion() {
  // If we're already waiting on a timeout to call, no-op out.
  if (newVersionCheckTimeout) {
    return;
  }
  // If called too recently, set a timeout to try again later.
  const diff = Date.now() - lastCheckedForNewVersion;
  if (diff < CHECK_VERSION_COOLDOWN) {
    newVersionCheckTimeout = setTimeout(() => {
      newVersionCheckTimeout = null;
      checkForNewVersion();
    }, diff);
    return;
  }
  // Fetch and compare hashes. If there's a mismatch, we're ready to update.
  lastCheckedForNewVersion = Date.now();
  fetch('/build-info.json')
    .then(res => {
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      return res.json();
    })
    .then(res => {
      if (res.COMMIT_SHA && res.COMMIT_SHA !== getCommitHash()) {
        store.dispatch(setIsNewVersionAvailable(true));
      }
    })
    .catch(err => {
      log.error('Failed to fetch build info', undefined, err);
    });
}

export default instance;
